import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
    CustomerAcquisitionsService,
    PolicyChangeRequestsCommonService,
    PolicyConsumerData,
    PolicyRequestsCommonService,
    PolicyRequestsData
} from '@nationwide/dgs-internet-servicing-policy-requests';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { mortgageConstant } from '../../CONSTANTS/tpi-common-constants';
import { EditMortgageServiceRequest } from '../../tpi-models/general/edit-mortgage-services-request';
import { MortgageCompaniesResponse } from '../../tpi-models/general/mortgage-companies-response';
import { StartPolicyChangeResponse } from '../../tpi-models/general/policy-change-models';
import { LoggerService } from '../logger/logger.service';
import { RequestMapperService } from './helper/request-mapper.service';

@Injectable({
    providedIn: 'root'
})
export class EditMortgageService {
    private customerAcquisitionsService = inject(CustomerAcquisitionsService);
    private loggerService = inject(LoggerService);
    private policyChangeRequestsService = inject(PolicyChangeRequestsCommonService);
    private policyRequestsCommonService = inject(PolicyRequestsCommonService);
    private requestMapper = inject(RequestMapperService);

    addMortgageOnPolicy(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        const consumerData: PolicyConsumerData = this.requestMapper.getConsumerData();
        const applyMortgageRequest = this.requestMapper.getApplyMortgagePropertyPolicyRequest(editMortgageServiceRequest);
        const policyRequestsData: PolicyRequestsData = {
            consumerData,
            productType: applyMortgageRequest.propertyType,
            policyId: applyMortgageRequest.request.policyChangeId,
            skipCache: applyMortgageRequest.skipCache
        };
        return this.policyChangeRequestsService.addMortgage(policyRequestsData,  [{ ...applyMortgageRequest.request.mortgage }]);
    }

    addOrUpdateMortgageCompany(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        this.loggerService.info('Property Mortgage Add/Update Started.', {
            editMortgageServiceRequest
        });
        return this.startPolicyChanges(editMortgageServiceRequest).pipe(
            switchMap((startPolicyChangeResponse: StartPolicyChangeResponse) => {
                if (startPolicyChangeResponse instanceof HttpErrorResponse) {
                    return throwError(() => startPolicyChangeResponse);
                } else {
                    editMortgageServiceRequest.policyChangeId = startPolicyChangeResponse.policyChangeId;
                    return this.retrieveAllMortgagesOnPolicy(editMortgageServiceRequest);
                }
            }),

            switchMap((retrieveAllMortgagesResponse: any) => {
                if (retrieveAllMortgagesResponse instanceof HttpErrorResponse) {
                    return throwError(() => retrieveAllMortgagesResponse);
                } else {
                    for (let index = 0; index < retrieveAllMortgagesResponse?.length; index++) {
                        if (retrieveAllMortgagesResponse[index]?.type === mortgageConstant.mortgagee1) {
                            editMortgageServiceRequest.mortgageCompany.mortgageId = retrieveAllMortgagesResponse[index]?.mortgageId;
                            if (retrieveAllMortgagesResponse[index]?.mortgageId.startsWith('p-') ||
                                retrieveAllMortgagesResponse[index]?.mortgageId.startsWith('P-')) {
                                return this.deleteAndApplyMortgageOnPolicy(editMortgageServiceRequest);
                            }
                            break;
                        }
                    }
                    return this.applyMortgageOnPolicy(editMortgageServiceRequest);
                }
            }),

            switchMap((applyMortgageOnPolicyResponse: any) => {
                if (applyMortgageOnPolicyResponse instanceof HttpErrorResponse) {
                    return throwError(() => applyMortgageOnPolicyResponse);
                } else {
                    return this.ratePolicyChanges(editMortgageServiceRequest);
                }
            }),

            switchMap((ratePolicyChangeResponse: any) => {
                if (ratePolicyChangeResponse instanceof HttpErrorResponse) {
                    return throwError(() => ratePolicyChangeResponse);
                } else {
                    return of(ratePolicyChangeResponse);
                }
            }),
            catchError((error) =>
                this.withdrawPolicyChanges(editMortgageServiceRequest).pipe(
                    switchMap((response: any) => throwError(() => response))
                )
            ),

            switchMap(() => this.submitPolicyChange(editMortgageServiceRequest)),

            switchMap((submitPolicyChangeResponse: any) => {
                if (submitPolicyChangeResponse instanceof HttpErrorResponse) {
                    return this.withdrawPolicyChanges(editMortgageServiceRequest).pipe(
                        switchMap(() => of({ error: true, response: submitPolicyChangeResponse }))
                    );
                } else {
                    return of({ error: false, editMortgageServiceRequest });
                }
            }),
            catchError((response: any) => of({ error: true, response }))
        );
    }

    applyMortgageOnPolicy(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        const consumerData: PolicyConsumerData = this.requestMapper.getConsumerData();
        const applyMortgageRequest = this.requestMapper.getApplyMortgagePropertyPolicyRequest(editMortgageServiceRequest);
        if (editMortgageServiceRequest.mortgageCompany?.mortgageId) {
            const policyRequestsData: PolicyRequestsData = {
                consumerData,
                productType: applyMortgageRequest.propertyType,
                policyId: applyMortgageRequest.request.policyChangeId,
                skipCache: applyMortgageRequest.skipCache,
                entityId: applyMortgageRequest.request.mortgageId as any
            };
            return this.policyChangeRequestsService.updateMortgage(policyRequestsData, applyMortgageRequest.request.mortgage);
        } else {
            return this.addMortgageOnPolicy(editMortgageServiceRequest);
        }
    }

    deleteAndApplyMortgageOnPolicy(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        this.loggerService.info('deleteAndApplyMortgageOnPolicy() starts', editMortgageServiceRequest);
        return this.deleteMortgagePropertyPolicy(editMortgageServiceRequest).pipe(
            switchMap((deleteMortgagePropertyPolicyResponse: any) =>
                this.deleteAndApplyMortgageOnPolicyProcessor(editMortgageServiceRequest, deleteMortgagePropertyPolicyResponse))
        );
    }
    deleteAndApplyMortgageOnPolicyProcessor(editMortgageServiceRequest: EditMortgageServiceRequest,
        deleteMortgagePropertyPolicyResponse: any): Observable<any> {
        if (deleteMortgagePropertyPolicyResponse instanceof HttpErrorResponse) {
            this.loggerService.info('deleteAndApplyMortgageOnPolicyProcessor() error in response', deleteMortgagePropertyPolicyResponse);
            return throwError((): HttpErrorResponse => deleteMortgagePropertyPolicyResponse);
        } else {
            this.loggerService.info('deleteAndApplyMortgageOnPolicyProcessor() response', deleteMortgagePropertyPolicyResponse);
            return this.addMortgageOnPolicy(editMortgageServiceRequest);
        }
    }

    deleteMortgagePropertyPolicy(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        const consumerData: PolicyConsumerData = this.requestMapper.getConsumerData();
        const deleteMortgageRequest = this.requestMapper.getDeleteMortgagePropertyPolicyRequest(editMortgageServiceRequest);
        const policyRequestsData: PolicyRequestsData = {
            consumerData,
            productType: deleteMortgageRequest.propertyType,
            policyId: deleteMortgageRequest.request.policyChangeId,
            payload: deleteMortgageRequest.request,
            skipCache: deleteMortgageRequest.skipCache
        };
        this.loggerService.info('deleteMortgagePropertyPolicy() starts', editMortgageServiceRequest);
        return this.policyChangeRequestsService.updatePolicyChange(policyRequestsData, deleteMortgageRequest.request);
    }

    getMortgageCompanies(mortgageName: string, mortgageState: string): Observable<MortgageCompaniesResponse> {
        const accessToken: string = sessionStorage.getItem('jwt');
        const consumerData: PolicyConsumerData = {
            accessToken,
            camSessionId: ''
        };
        const skipCache = true;
        return this.customerAcquisitionsService.retrieveAllMortgageCompaniesInfo({
            consumerData,
            skipCache,
            mortgageCompanyName: mortgageName,
            state: mortgageState
        });
    }

    ratePolicyChanges(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        const consumerData: PolicyConsumerData = this.requestMapper.getConsumerData();
        const ratePolicyChangeRequest = this.requestMapper.getRateOrSubmitPropertyPolicyChangeRequest(editMortgageServiceRequest);
        const policyRequestsData: PolicyRequestsData = {
            consumerData,
            productType: ratePolicyChangeRequest.propertyType,
            policyId: ratePolicyChangeRequest.request.policyChangeId,
            skipCache: ratePolicyChangeRequest.skipCache
        };
        return this.policyChangeRequestsService.ratePolicyChange(policyRequestsData);
    }

    retrieveAllMortgagesOnPolicy(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        const consumerData: PolicyConsumerData = this.requestMapper.getConsumerData();
        const retrieveAllMortgagesRequest = this.requestMapper.getRetrieveAllMortgagesRequest(editMortgageServiceRequest);
        const policyRequestsData: PolicyRequestsData = {
            consumerData,
            productType: retrieveAllMortgagesRequest.propertyType,
            policyId: retrieveAllMortgagesRequest.request.policyNumber,
            skipCache: retrieveAllMortgagesRequest.skipCache
        };
        return this.policyRequestsCommonService.getPolicyMortgages(policyRequestsData);
    }

    startPolicyChanges(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        const consumerData: PolicyConsumerData = this.requestMapper.getConsumerData();
        const startPolicyChangeRequest = this.requestMapper.getStartPolicyChangeRequest(editMortgageServiceRequest);
        const policyRequestsData: PolicyRequestsData = {
            consumerData,
            productType: startPolicyChangeRequest.propertyType,
            policyId: startPolicyChangeRequest.request.policyNumber,
            effectiveDate: startPolicyChangeRequest.request.effectiveDate,
            skipCache: startPolicyChangeRequest.skipCache,
            payload: startPolicyChangeRequest.request
        };
        return this.policyChangeRequestsService.startPolicyChange(policyRequestsData);
    }

    submitPolicyChange(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        const consumerData: PolicyConsumerData = this.requestMapper.getConsumerData();
        const submitPropertyPolicyChangeRequest = this.requestMapper.getRateOrSubmitPropertyPolicyChangeRequest(editMortgageServiceRequest);
        const policyRequestsData: PolicyRequestsData = {
            consumerData,
            productType: submitPropertyPolicyChangeRequest.propertyType,
            policyId: submitPropertyPolicyChangeRequest.request.policyNumber,   // policy number, not change id
            skipCache: submitPropertyPolicyChangeRequest.skipCache
        };
        return this.policyRequestsCommonService.issuePolicyChange(   // use issuePolicyChange on policyRequestsCommonService
            policyRequestsData,
            { policyChangeId: editMortgageServiceRequest.policyChangeId }
        );
    }

    withdrawPolicyChanges(editMortgageServiceRequest: EditMortgageServiceRequest): Observable<any> {
        this.loggerService.info('withdrawPolicyChanges() property policy starts..', editMortgageServiceRequest);
        if (!editMortgageServiceRequest?.policyChangeId) {
            this.loggerService.info('withdrawPolicyChanges() policy change id is null', editMortgageServiceRequest);
            return of({ result: 'ok' });
        }
        const consumerData: PolicyConsumerData = this.requestMapper.getConsumerData();
        const policyRequestsData: PolicyRequestsData = {
            consumerData,
            productType: editMortgageServiceRequest.policyType,
            policyId: editMortgageServiceRequest.policyChangeId
        };
        return this.policyChangeRequestsService.deletePolicyChange(policyRequestsData);
    }
}